const APPROX_TOKENS_PER_WORD = 1.42;
const ANALYSIS_TOKEN_MULTIPLIER = 7;
const BILLABLE_TOKENS_PER_IMPORT_CREDIT = 5_000;

type StoryImportPricingConfig = {
  analysisTokenMultiplier?: number | null;
  minimumCreditCost?: number | null;
  tokensPerCredit?: number | null;
  tokensPerWord?: number | null;
};

function normalizeStoryImportPricingConfig(
  input: StoryImportPricingConfig | number | null | undefined,
) {
  if (typeof input === "number") {
    return {
      analysisTokenMultiplier: ANALYSIS_TOKEN_MULTIPLIER,
      minimumCreditCost: input,
      tokensPerCredit: BILLABLE_TOKENS_PER_IMPORT_CREDIT,
      tokensPerWord: APPROX_TOKENS_PER_WORD,
    };
  }

  return {
    analysisTokenMultiplier:
      typeof input?.analysisTokenMultiplier === "number" && input.analysisTokenMultiplier > 0
        ? input.analysisTokenMultiplier
        : ANALYSIS_TOKEN_MULTIPLIER,
    minimumCreditCost:
      typeof input?.minimumCreditCost === "number" && input.minimumCreditCost > 0
        ? Math.trunc(input.minimumCreditCost)
        : 25,
    tokensPerCredit:
      typeof input?.tokensPerCredit === "number" && input.tokensPerCredit > 0
        ? Math.trunc(input.tokensPerCredit)
        : BILLABLE_TOKENS_PER_IMPORT_CREDIT,
    tokensPerWord:
      typeof input?.tokensPerWord === "number" && input.tokensPerWord > 0
        ? input.tokensPerWord
        : APPROX_TOKENS_PER_WORD,
  };
}

export function estimateStoryImportSourceTokens(
  wordCount: number | null | undefined,
  config?: StoryImportPricingConfig | null,
) {
  const pricing = normalizeStoryImportPricingConfig(config);
  const normalizedWordCount = Number.isFinite(wordCount) && (wordCount ?? 0) > 0
    ? Math.trunc(wordCount ?? 0)
    : 0;

  if (normalizedWordCount === 0) {
    return 0;
  }

  return Math.ceil(normalizedWordCount * pricing.tokensPerWord);
}

export function estimateStoryImportAnalysisTokens(
  wordCount: number | null | undefined,
  config?: StoryImportPricingConfig | null,
) {
  const pricing = normalizeStoryImportPricingConfig(config);
  const estimatedSourceTokens = estimateStoryImportSourceTokens(wordCount, pricing);

  if (estimatedSourceTokens === 0) {
    return 0;
  }

  return Math.ceil(estimatedSourceTokens * pricing.analysisTokenMultiplier);
}

export function calculateStoryImportCreditCost(
  wordCount: number | null | undefined,
  config: StoryImportPricingConfig | number,
) {
  const pricing = normalizeStoryImportPricingConfig(config);
  const normalizedWordCount = Number.isFinite(wordCount) && (wordCount ?? 0) > 0
    ? Math.trunc(wordCount ?? 0)
    : 0;

  if (normalizedWordCount === 0) {
    return pricing.minimumCreditCost;
  }

  const estimatedBillableTokens = estimateStoryImportAnalysisTokens(normalizedWordCount, pricing);

  return Math.max(
    pricing.minimumCreditCost,
    Math.ceil(estimatedBillableTokens / pricing.tokensPerCredit),
  );
}
